import type { WorkRepresentative } from "@ondo/shared";

import { initialsOf } from "../lib/names.ts";
import { Tag } from "../lib/Tag.tsx";
import { WORK_REPRESENTATION_COPY } from "./work.constants.ts";
import type { RepresentationPanel } from "./work.model.ts";
import { describeRepresentationTerm } from "./work.model.ts";

/**
 * Who represents this work, as a VIEWER reads it — OND-73.
 *
 * The buyer's question. Somebody has shown them a code and says they act for
 * the owner; once let in, the record answers who actually does, under two
 * headings so that an ended or expired appointment is drawn as what it is and
 * can never read as current. The rows are the owner's agents panel's rows on
 * purpose — same tile, same name-over-meta — because they answer the same shape
 * of question; what this panel never carries is a control, a note or a term.
 *
 * REPRESENTATION IS NOT OWNERSHIP, and the panel says so in a sentence rather
 * than trusting the eyebrow to carry it: the ticket's completion criterion is
 * that the display "must distinguish representation from ownership".
 *
 * Renders nothing at all when hidden — for the owner, whose agents panel
 * already lists them, and for an empty answer, which a work with no
 * representative and a caller the work does not reach both get. A section with
 * a heading and no rows would tell those two apart, and must not.
 */
export function WorkRepresentationPanel({ panel }: { panel: RepresentationPanel }) {
  if (panel.kind === "hidden") return null;

  return (
    <section className="work__agents" aria-labelledby="work-representation-title">
      <header className="work__agents-head">
        <h2 id="work-representation-title" className="work__agents-title">
          {WORK_REPRESENTATION_COPY.title}
        </h2>
      </header>

      {panel.kind === "loading" ? (
        <p className="work__agents-empty" role="status">
          {panel.message}
        </p>
      ) : (
        <>
          {panel.current.length > 0 && (
            <>
              <h3 className="work__representation-group">{WORK_REPRESENTATION_COPY.current}</h3>
              <ul className="work__agents-list">
                {panel.current.map((row) => (
                  <Representative key={row.mandateId} row={row} />
                ))}
              </ul>
            </>
          )}
          {panel.past.length > 0 && (
            <>
              <h3 className="work__representation-group">{WORK_REPRESENTATION_COPY.past}</h3>
              <ul className="work__agents-list work__representation-past">
                {panel.past.map((row) => (
                  <Representative key={row.mandateId} row={row} />
                ))}
              </ul>
            </>
          )}
          <p className="work__representation-note">{WORK_REPRESENTATION_COPY.notTheOwner}</p>
        </>
      )}
    </section>
  );
}

function Representative({ row }: { row: WorkRepresentative }) {
  return (
    <li className="work__agent">
      <span className="work__agent-mark" aria-hidden="true">
        {initialsOf(row.displayName, "·")}
      </span>
      <span className="work__agent-body">
        <span className="work__agent-name">{row.displayName}</span>
        <span className="work__agent-meta">
          {row.ondoId}
          {row.organisation
            ? ` · ${WORK_REPRESENTATION_COPY.forFirm(row.organisation.displayName)}`
            : ""}
        </span>
        {/* The dates, from the record. The standing decides the word, and the
            standing is SQL's — nothing here counts days or compares clocks. */}
        <span className="work__agent-term">{describeRepresentationTerm(row)}</span>
      </span>
      {row.verified && <Tag tone="verified">{WORK_REPRESENTATION_COPY.verified}</Tag>}
      {/* The one permission a buyer can act on, shown only while it stands. */}
      {row.standing === "current" && row.mayConsign && (
        <Tag tone="neutral">{WORK_REPRESENTATION_COPY.mayConsign}</Tag>
      )}
    </li>
  );
}
